<script setup lang="ts">
import { PropType } from 'vue'

/* Utils */
import { defineLayoutOfProduct } from '@/utils/defineLayoutOfProduct'

/* Stores */
import { useCityStore } from '@/store/city/city'

/* Types */
import { Product, Modification } from '@/types/api/api-v3/product'
import { NAME_PARENT_COMPONENT } from '@/types/products'

const emit = defineEmits(['openDialogProduct'])

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  currentModification: {
    type: Object as PropType<Modification>,
    required: true,
  },
  parentComponent: {
    type: String,
    required: true,
  },
  wrapperAttrs: {
    type: Object,
    default: () => {},
  },
  wrapperComponent: {
    type: String,
    default: '',
  },
  isMainPage: {
    type: Boolean,
    default: false,
  },
})

const { isDesktop, isMobileOrTablet } = useDevice()

const {
  cityInfo: { padeg },
} = useCityStore()

const productTitle = computed(() => {
  if (props.product.name === props.currentModification.name) {
    return `Заказать "${props.product.name}" с доставкой в ${padeg?.pred} от ресторана Farfor`
  }
  return `Заказать "${props.product.name} ${props.currentModification.name}" с доставкой в ${padeg?.pred} от ресторана Farfor`
})

const isImageCrashed = ref(false)

const checkVisibleQuantity = computed(() =>
  Boolean(
    props.product.elements && props.product.category_slug?.includes('sety'),
  ),
)

const isHighLight = computed(() =>
  props.product.tags.find((item) => item.as_highlight === true),
)

const isShowDiscount = computed(() => !!props.currentModification.discount)

const productImageClasses = computed(() => {
  return [
    'product__image',
    {
      'product__image--mobile':
        NAME_PARENT_COMPONENT.DIALOG === props.parentComponent &&
        isMobileOrTablet,
      'product__image--dialog-desktop':
        NAME_PARENT_COMPONENT.DIALOG === props.parentComponent && isDesktop,
    },
  ]
})

const openDialogProduct = () => {
  emit('openDialogProduct')
}

const getProductMicrolayouts = defineLayoutOfProduct(props.parentComponent)

const itemTypeProductItem = getProductMicrolayouts('image')
</script>

<template>
  <component
    :is="wrapperComponent"
    :class="productImageClasses"
    v-bind="wrapperAttrs"
    v-on="isMainPage ? { click: openDialogProduct } : {}"
    @click.prevent
  >
    <nuxt-img
      v-if="product.image && !isImageCrashed"
      :src="product.image"
      :alt="product.name"
      :title="productTitle"
      :itemprop="itemTypeProductItem"
      class="image"
      loading="lazy"
      format="webp"
      width="100%"
      height="100%"
      @error="isImageCrashed = true"
    />

    <nuxt-img
      v-else
      :alt="product.name"
      :title="productTitle"
      src="/no-image-category.webp"
      format="webp"
      width="100%"
      height="100%"
      loading="lazy"
    />

    <div v-if="checkVisibleQuantity" class="product__image-quantity">
      {{ product.elements }} шт.
    </div>

    <span v-if="isShowDiscount" class="product__image-discount">
      -{{ currentModification.discount }}%
    </span>

    <template v-if="NAME_PARENT_COMPONENT.DIALOG === parentComponent">
      <div
        v-if="isHighLight"
        :style="{ 'background-color': isHighLight.hover_background_color }"
        class="product__image-sale"
      >
        {{ isHighLight.name }}
      </div>
    </template>
  </component>
</template>

<style scoped lang="scss">
.product__image {
  img {
    width: 100%;
    border-radius: 20px;
  }
}

@mixin quantity {
  position: absolute;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 800;
  color: $white;
  background: $text-dark-gray;
  border-radius: 200px;
}

@mixin quantity-tablet {
  width: initial;
  padding: 2px 4px;
  margin-top: 24px;
  font-size: 10px;
  line-height: 12px;
}

@mixin discount-styles {
  position: absolute;
  top: 0;
  right: 0;
  font-weight: 800;
  color: #000;
  background: $discount-yellow;
}

@mixin product-main-desktop {
  .product__image {
    position: relative;
    grid-area: 1 / 1 / 2 / 3;
    height: max-content;
    min-height: 187px;
    max-height: 270px;
    transition: 0.2s ease-in;

    &:hover {
      cursor: pointer;
      transition: 0.2s ease-in;
      transform: translateY(5px);
    }
  }

  .product__image-quantity {
    @include quantity;

    bottom: 18px;

    @include media('<=tablet') {
      @include quantity-tablet;
    }
  }

  .product__image-discount {
    @include discount-styles;

    padding: 6px 12px;
    font-size: 12px;
    line-height: 14px;
    border-radius: 20px;
  }
}

@mixin product-main-mobile {
  .product__image {
    position: relative;
    grid-area: 1 / 1 / 2 / 2;
    height: max-content;
    min-height: 100px;
    max-height: 270px;
    transition: 0.2s ease-in;
  }

  .product__image-quantity {
    @include quantity;

    bottom: 10px;

    @include media('<=tablet') {
      @include quantity-tablet;
    }
  }

  .product__image-discount {
    @include discount-styles;

    padding: 3px 5px;
    margin-left: 10px;
    font-size: 10px;
    line-height: 12px;
    border-radius: 20px;
  }
}

@mixin product-dialog {
  .product__image {
    position: relative;
    width: 426px;
    height: 100%;

    &--mobile {
      width: inherit;
      height: initial;
      margin: 0 48px;
      overflow: visible;

      @include media('<=tablet') {
        margin: 0 20px;
      }
    }

    &--dialog-desktop {
      height: 284px;
    }
  }

  .product__image-quantity {
    @include quantity;

    bottom: 0;

    @include media('<=tablet') {
      width: initial;
      margin-top: 24px;
    }
  }

  .product__image-sale {
    position: absolute;
    bottom: 0;
    left: 0;
    justify-content: center;
    width: fit-content;
    padding: 7px 10px;
    font-size: 12px;
    font-weight: bold;
    line-height: 12px;
    color: $white;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: $success;
    border-radius: 20px;

    @include media('<=tablet') {
      position: relative;
      padding: 6px 10px;
    }
  }

  .product__image-discount {
    position: absolute;
    top: 0;
    right: 0;
    padding: 6px 12px;
    font-size: 16px;
    font-weight: 800;
    line-height: 19px;
    background: $discount-yellow;
    border-radius: 20px;

    @include media('<=tablet') {
      padding: 5px 12px;
      font-size: 12px;
      line-height: 14px;
    }
  }
}

@mixin product-page {
  .product__image {
    position: relative;
    max-height: 452px;
    border-radius: 32px;

    @include media('<=desktop') {
      margin-bottom: 15px;
    }

    @include media('>=tablet', '<=desktop-mini') {
      max-width: 100%;
      max-height: 100%;
    }
  }

  .product__image-discount {
    @include discount-styles;

    padding: 6px 10px;
    font-size: 16px;
    line-height: 19px;
    border-radius: 40px;

    @include media('<desktop') {
      padding: 5px 12px;
      font-size: 12px;
      line-height: 14px;
      border-radius: 20px;
    }
  }

  .product__image-quantity {
    @include quantity;

    bottom: 18px;
    padding: 3px 12px;
    font-size: 18px;
    line-height: 29px;

    @include media('<=tablet') {
      @include quantity-tablet;
    }
  }
}

.product--main-desktop {
  @include product-main-desktop();
}

.product--main-mobile {
  @include product-main-mobile;
}

.product--dialog {
  @include product-dialog;
}

.product--page {
  @include product-page;
}
</style>
